import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearImpersonation, getImpersonation, IMPERSONATION_EVENT, isImpersonationExpired } from '../../../auth/impersonation';
import './ImpersonationBanner.css';

/**
 * Always-visible strip while an ops person is seeing the dashboard as someone
 * else. Shows who is being impersonated, by whom, and when the session ends;
 * when the token expires the app is covered and the tab must be closed.
 */
const ImpersonationBanner: React.FC = () => {
    const { t } = useTranslation();
    const [imp, setImp] = useState(() => getImpersonation());
    const [expired, setExpired] = useState(() => (imp ? isImpersonationExpired(imp) : false));

    // Follow the session: /impersonate#ended clears it without a reload.
    useEffect(() => {
        const sync = () => {
            const next = getImpersonation();
            setImp(next);
            setExpired(next ? isImpersonationExpired(next) : false);
        };
        window.addEventListener(IMPERSONATION_EVENT, sync);
        return () => window.removeEventListener(IMPERSONATION_EVENT, sync);
    }, []);

    useEffect(() => {
        if (!imp) return;
        document.body.classList.add('gs-impersonating');
        const id = window.setInterval(() => {
            if (isImpersonationExpired(imp)) {
                setExpired(true);
                window.clearInterval(id);
            }
        }, 15_000);
        return () => {
            window.clearInterval(id);
            document.body.classList.remove('gs-impersonating');
        };
    }, [imp]);

    if (!imp) return null;

    const exit = () => {
        clearImpersonation();
        setImp(null);
        window.location.replace('/impersonate#ended');
    };

    const until = new Date(imp.exp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return (
        <>
            <div className="gs-impersonation-banner" role="status" data-cy="impersonation-banner">
                <span className="gs-impersonation-banner__text">
                    {t('impersonation.viewingAs', { user: imp.targetEmail || imp.sub })}
                    {imp.actorEmail && <> · {t('impersonation.by', { actor: imp.actorEmail })}</>}
                    {' · '}
                    {t('impersonation.expiresAt', { time: until })}
                </span>
                <button type="button" className="gs-impersonation-banner__exit" onClick={exit}>
                    {t('impersonation.exit')}
                </button>
            </div>
            {expired && (
                <div className="gs-impersonation-expired" role="alertdialog" data-cy="impersonation-expired">
                    <div className="gs-impersonation-expired__card">
                        <h2>{t('impersonation.expiredTitle')}</h2>
                        <p>{t('impersonation.expiredText')}</p>
                        <button type="button" className="gs-impersonation-banner__exit" onClick={exit}>
                            {t('impersonation.exit')}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ImpersonationBanner;
